import React from 'react';
import { motion } from 'framer-motion';
import CardFace from './cards/CardFace';
import { CARD_BACK, CARD_ASPECT } from '../cardArt';
import { cardLabel } from '../game/cards';

// A single Neapolitan card. The card look (frame, ivory face, border, margins)
// comes entirely from the shared master template, so all 40 cards are uniform
// and there is no "card inside a card" effect. Interactive cards are
// role="text" containers (tabbable + tap/keyboard activated) so VoiceOver reads
// only the aria-label (no "pulsante" trait) while double-tap still activates.
export default function NeapolitanCard({
  card,
  faceDown = false,
  selected = false,
  interactive = false,
  disabled = false,
  onClick,
  className = '',
  sizeClass = 'w-16 sm:w-20',
  testId,
  ariaLabel,
  felt = false,
}) {
  if (faceDown || !card) {
    return (
      <div className={`${sizeClass} ${className}`} aria-hidden="true" data-testid={testId}>
        <div
          className="w-full relative overflow-hidden"
          style={{
            aspectRatio: CARD_ASPECT,
            borderRadius: '7%',
            boxShadow: felt
              ? '0 10px 20px rgba(0,0,0,0.45)'
              : '0 8px 16px rgba(0,0,0,0.28)',
          }}
        >
          <img
            src={CARD_BACK}
            alt=""
            className="w-full h-full object-cover"
            draggable={false}
          />
        </div>
      </div>
    );
  }

  const label = ariaLabel || cardLabel(card);

  if (interactive) {
    const onKey = (e) => {
      if (onClick && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        onClick(e);
      }
    };
    return (
      <motion.div
        role="text"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={onKey}
        data-testid={testId}
        aria-label={label}
        whileTap={{ scale: 0.96 }}
        animate={{ y: selected ? -14 : 0 }}
        transition={{ type: 'spring', stiffness: 400, damping: 28 }}
        style={{
          borderRadius: '9%',
          cursor: 'pointer',
          boxShadow: selected ? '0 0 0 3px #D4AF37, 0 16px 26px rgba(0,0,0,0.5)' : undefined,
        }}
        className={`napo-card ${sizeClass} ${className} relative outline-none ${disabled ? 'opacity-60' : ''}`}
      >
        <span className="sr-only">{label}</span>
        <CardFace card={card} sizeClass="w-full" felt={felt} />
      </motion.div>
    );
  }

  return (
    <div className={`${sizeClass} ${className}`} role="text" aria-label={label} data-testid={testId}>
      <span className="sr-only">{label}</span>
      <CardFace card={card} sizeClass="w-full" felt={felt} />
    </div>
  );
}
